import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MyDogDocuments from './MyDogDocuments';
import ConfirmationModal from './ConfirmationModal';
import AttributeLabel from './AttributeLabel';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import {
  PlusIcon,
  CameraIcon,
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  ScissorsIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FolderOpenIcon,
  PuzzlePieceIcon,
  HomeModernIcon,
} from '@heroicons/react/24/outline';
import SyringeIcon from './icons/SyringeIcon';
import { FaDog } from 'react-icons/fa';
import { format, differenceInYears } from 'date-fns';
import { de } from 'date-fns/locale';

// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Dog {
  id?: string;  // Some APIs use 'id'
  hunde_id: string;  // Some APIs use 'hunde_id'
  hundename: string;
  rufname?: string;  // Alternative name field
  vollstaendiger_name?: string;  // Full name field
  rasse?: string;
  geburtsdatum?: string;
  geburtstag?: string;  // Alternative date field
  geschlecht?: string | number | null;
  kastriert?: boolean | string | number | null;
  chip_nummer?: string;
  chipnummer?: string;  // Alternative field
  pass_nr?: string;
  tassonummer?: string;  // Alternative field
  tollwut?: string;
  tollwut_impfung_ablauf?: string;  // New field name from API
  impfstatus_tollwut?: string;  // Legacy field
  valid_tollwut?: boolean;
  versicherung?: string;
  insurance_name?: string;  // Alternative field
  valid_insurance?: boolean;
  foto_path?: string;
  image_path?: string;  // Alternative image field
  image_path_updated?: number;
  imageTimestamp?: number;
  problems?: string[];
  hundebox?: string | number | boolean | null;
  extras?: string;
}

interface DogModalProps {
  dog?: Dog;
  isOpen: boolean;
  onClose: () => void;
  onSave: (message?: string) => void;
}

const DogModal: React.FC<DogModalProps> = ({ dog, isOpen, onClose, onSave }) => {
  const { BASE_URL } = useContext(AuthContext);
  const [formData, setFormData] = useState<any>({
    rufname: '',
    rasse: '',
    geburtstag: '',
    geschlecht: 'Männlich',
    kastriert: '',
    chipnummer: '',
    tassonummer: '',
    tollwut_impfung_ablauf: '',
    insurance_name: '',
    hundebox: '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (dog) {
      const birthDate = dog.geburtstag || dog.geburtsdatum;
      const tollwutDate = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
      
      setFormData({
        rufname: dog.rufname || dog.hundename || '',
        rasse: dog.rasse || '',
        geburtstag: birthDate ? birthDate.split('T')[0] : '',
        geschlecht: dog.geschlecht === 1 || dog.geschlecht === 'weiblich' || dog.geschlecht === 'Weiblich' ? 'Weiblich' : 
                    dog.geschlecht === 0 || dog.geschlecht === 'männlich' || dog.geschlecht === 'Männlich' ? 'Männlich' : '',
        kastriert: dog.kastriert === true || dog.kastriert === 1 || dog.kastriert === 'true' || dog.kastriert === 'Ja' || dog.kastriert === 'ja' ? 'ja' : 
                   dog.kastriert === false || dog.kastriert === 0 || dog.kastriert === 'false' || dog.kastriert === 'Nein' || dog.kastriert === 'nein' ? 'nein' : '',
        chipnummer: dog.chipnummer || dog.chip_nummer || '',
        tassonummer: dog.tassonummer || dog.pass_nr || '',
        tollwut_impfung_ablauf: tollwutDate ? tollwutDate.split('T')[0] : '',
        insurance_name: dog.insurance_name || dog.versicherung || '',
        hundebox: dog.hundebox === 1 || dog.hundebox === true || dog.hundebox === 'ja' || dog.hundebox === 'Ja' ? 'ja' :
                  dog.hundebox === 0 || dog.hundebox === false || dog.hundebox === 'nein' || dog.hundebox === 'Nein' ? 'nein' : '',
      });
      // Set image preview if dog has image
      if (dog.foto_path || dog.image_path) {
        setImagePreview(`${BASE_URL}/api/dogs/image-url/${dog.hunde_id || dog.id}?token=${localStorage.getItem('jwt_token')}&ts=${dog.image_path_updated || Date.now()}`);
        setImageLoadError(false);
      }
    } else {
      setFormData({
        rufname: '',
        rasse: '',
        geburtstag: '',
        geschlecht: 'Männlich',
        kastriert: '',
        chipnummer: '',
        tassonummer: '',
        tollwut_impfung_ablauf: '',
        insurance_name: '',
        hundebox: '',
      });
      setImagePreview(null);
      setImageLoadError(false);
    }
    setImageFile(null);
  }, [dog, isOpen, BASE_URL]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageLoadError(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();

      // Add all form fields - only send non-empty values when editing
      Object.keys(formData).forEach(key => {
        const value = formData[key];

        // Skip empty fields when editing (but send them when creating new dog)
        if (dog && (!value || value === '')) {
          return; // Skip this field when editing
        }

        if (key === 'kastriert') {
          // Convert to 1/0/null for MySQL backend
          if (value === 'ja') {
            submitData.append(key, '1');
          } else if (value === 'nein') {
            submitData.append(key, '0');
          } else if (!dog) {
            // Only send empty string for new dogs, skip for edits
            submitData.append(key, '');
          }
        } else if (key === 'geschlecht') {
          // Convert to 0 for männlich, 1 for weiblich, null for not specified
          if (value === 'Männlich') {
            submitData.append(key, '0');
          } else if (value === 'Weiblich') {
            submitData.append(key, '1');
          } else if (!dog) {
            // Only send empty string for new dogs, skip for edits
            submitData.append(key, '');
          }
        } else if (key === 'hundebox') {
          // Convert hundebox to 1/0/null for MySQL backend
          if (value === 'ja' || value === 'Ja') {
            submitData.append(key, '1');
          } else if (value === 'nein' || value === 'Nein') {
            submitData.append(key, '0');
          } else if (!dog) {
            // Only send empty string for new dogs, skip for edits
            submitData.append(key, '');
          }
        } else {
          // For all other fields, only append if not empty when editing
          if (!dog || value) {
            submitData.append(key, value || '');
          }
        }
      });

      // Add image if present
      if (imageFile) {
        submitData.append('image', imageFile);
      }

      const endpoint = dog ? `/api/dogs/${dog.hunde_id}` : '/api/dogs/add_dog';
      const response = await apiClient[dog ? 'put' : 'post'](endpoint, submitData, {
        headers: {
          'Content-Type': undefined as any  // This forces axios to remove the default JSON content-type
        }
      });

      if (response.data.success) {
        onSave(dog ? 'Hund erfolgreich aktualisiert' : 'Hund erfolgreich hinzugefügt');
        onClose();
      } else {
        setError(response.data.message || 'Ein Fehler ist aufgetreten');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ein Netzwerkfehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    
    if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
      years--;
      months = months < 0 ? 12 + months : 11;
    }
    
    if (years === 0 && months === 0) return 'Welpe';
    if (years === 0) return `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
    return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}${months > 0 ? ` ${months} ${months === 1 ? 'Monat' : 'Monate'}` : ''}`;
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      variant="form"
      size="lg"
      title={`"${formData.rufname || 'Max'}" ${dog ? 'bearbeiten' : 'hinzufügen'}`}
      icon={DogIcon}
      accent="brand"
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: onClose, disabled: loading }}
          primary={{
            label: loading ? 'Speichert...' : dog ? 'Speichern' : 'Hinzufügen',
            onClick: () => formRef.current?.requestSubmit(),
            loading,
          }}
        />
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 text-error-600 dark:text-error-300 rounded-lg flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left side - Image section */}
        <div className="relative lg:w-2/5 lg:flex-shrink-0">
          <div className="relative h-64 sm:h-72 lg:h-full lg:min-h-[380px] overflow-hidden rounded-2xl bg-gray-200 dark:bg-gray-900">
            {imagePreview && !imageLoadError ? (
              <img
                src={imagePreview}
                alt={formData.rufname || 'Hund'}
                className="w-full h-full object-cover"
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <div className="w-full h-full bg-primary-600 flex flex-col items-center justify-center p-8">
                <DogIcon className="h-24 w-24 text-white/50 mb-4" />
                <p className="text-white/70 text-center">Kein Bild vorhanden</p>
              </div>
            )}

            {/* Overlay with dog info */}
            <div className="absolute inset-0 bg-black/50" />

            {/* Profile completion indicator */}
            <div className="absolute top-4 left-4">
              <div className={`backdrop-blur-md rounded-full px-3 py-2 flex items-center gap-2 shadow-lg ${
                dog && formData.valid_insurance && formData.valid_tollwut
                  ? 'bg-success-500/80'
                  : 'bg-amber-500/80'
              }`}>
                {dog && formData.valid_insurance && formData.valid_tollwut ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                    <span className="text-white text-sm font-semibold">Vollständig</span>
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-5 w-5 text-white animate-pulse" />
                    <span className="text-white text-sm font-semibold">Unvollständig</span>
                  </>
                )}
              </div>
            </div>

            {/* Dog name and info at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h2 className="text-2xl font-bold mb-2">{formData.rufname || 'Name'}</h2>
              <p className="text-sm opacity-90">
                {formData.rasse || 'Rasse'} • {formData.geburtstag ? calculateAge(formData.geburtstag) : 'Alter'}
              </p>
              {dog && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.valid_tollwut && (
                    <span className="inline-flex items-center bg-success-500/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                      <SyringeIcon className="h-3.5 w-3.5 mr-1.5" />
                      Geimpft
                    </span>
                  )}
                  {formData.valid_insurance && (
                    <span className="inline-flex items-center bg-info-500/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                      <ShieldCheckIcon className="h-3.5 w-3.5 mr-1.5" />
                      Versichert
                    </span>
                  )}
                  {formData.geschlecht && (
                    <span className="inline-flex items-center bg-primary-500/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                      {formData.geschlecht === 'Weiblich' ? '♀' : '♂'} {formData.geschlecht}
                    </span>
                  )}
                  {formData.kastriert && (
                    <span className="inline-flex items-center bg-warning-500/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                      <ScissorsIcon className="h-3.5 w-3.5 mr-1.5" />
                      Kastriert
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Upload button */}
          <label className="absolute bottom-4 right-4 cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            <div className="bg-white/90 hover:bg-white text-gray-700 p-3 rounded-full shadow-lg transition-all hover:scale-110">
              <CameraIcon className="h-5 w-5" />
            </div>
          </label>
        </div>

        {/* Right side - Form fields */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 lg:w-3/5 space-y-6">
          <ModalSection title="Basisdaten">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="name" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rufname *
                </AttributeLabel>
                <input
                  type="text"
                  required
                  value={formData.rufname}
                  onChange={(e) => setFormData({ ...formData, rufname: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                  placeholder="Max"
                />
              </div>

              <div>
                <AttributeLabel iconKey="breed" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rasse
                </AttributeLabel>
                <input
                  type="text"
                  value={formData.rasse}
                  onChange={(e) => setFormData({ ...formData, rasse: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                  placeholder="Labrador"
                />
              </div>

              <div>
                <AttributeLabel iconKey="birthday" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Geburtsdatum
                </AttributeLabel>
                <input
                  type="date"
                  value={formData.geburtstag}
                  onChange={(e) => setFormData({ ...formData, geburtstag: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                />
              </div>

              <div>
                <AttributeLabel iconKey="gender" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Geschlecht
                </AttributeLabel>
                <select
                  value={formData.geschlecht}
                  onChange={(e) => setFormData({ ...formData, geschlecht: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="Männlich">Männlich</option>
                  <option value="Weiblich">Weiblich</option>
                </select>
              </div>

              <div>
                <AttributeLabel iconKey="neutered" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Kastriert
                </AttributeLabel>
                <select
                  value={formData.kastriert || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    kastriert: e.target.value
                  })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="ja">Ja</option>
                  <option value="nein">Nein</option>
                </select>
              </div>
            </div>
          </ModalSection>

          <ModalSection title="Registrierung & Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="chipNumber" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Chipnummer
                </AttributeLabel>
                <input
                  type="text"
                  value={formData.chipnummer}
                  onChange={(e) => setFormData({ ...formData, chipnummer: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                  placeholder="276000000000000"
                />
              </div>

              <div>
                <AttributeLabel iconKey="tassoNumber" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tasso-Nummer
                </AttributeLabel>
                <input
                  type="text"
                  value={formData.tassonummer}
                  onChange={(e) => setFormData({ ...formData, tassonummer: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                  placeholder="123456789"
                />
              </div>

              <div>
                <AttributeLabel iconKey="vaccination" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tollwutimpfung (gültig bis)
                </AttributeLabel>
                <input
                  type="date"
                  value={formData.tollwut_impfung_ablauf}
                  onChange={(e) => setFormData({ ...formData, tollwut_impfung_ablauf: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                />
              </div>

              <div>
                <AttributeLabel iconKey="liability" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Versicherung (Name)
                </AttributeLabel>
                <input
                  type="text"
                  value={formData.insurance_name}
                  onChange={(e) => setFormData({ ...formData, insurance_name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                  placeholder="Allianz, HUK-Coburg, etc."
                />
              </div>

              <div className="md:col-span-2">
                <AttributeLabel iconKey="dogBox" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hundebox vorhanden
                </AttributeLabel>
                <select
                  value={formData.hundebox || ''}
                  onChange={(e) => setFormData({ ...formData, hundebox: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-success-500 focus:border-success-500 transition-colors"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="ja">Ja</option>
                  <option value="nein">Nein</option>
                </select>
              </div>
            </div>
          </ModalSection>
        </form>
      </div>
    </ModalShell>
  );
};

const MyDogs: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDog, setEditingDog] = useState<Dog | undefined>(undefined);
  const [expandedDog, setExpandedDog] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });
  const [dogDocumentStatus, setDogDocumentStatus] = useState<{[key: string]: { missingDocuments: any[], isVaccinationExpired: boolean }}>({});

  const checkDogDocuments = useCallback(async (dogId: string) => {
    try {
      const response = await apiClient.get(`/api/dogs/check_required_documents/${dogId}`);
      return {
        missingDocuments: response.data.missing_documents || [],
        success: response.data.success
      };
    } catch (error) {
      console.error('Error checking dog documents:', error);
      return { missingDocuments: [], success: true };
    }
  }, []);

  const checkVaccinationExpiry = useCallback((vaccinationDate: string | undefined) => {
    if (!vaccinationDate) return false;
    const vaccDate = new Date(vaccinationDate);
    const today = new Date();
    return vaccDate < today;
  }, []);

  const loadDogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/dogs/get_user_dogs');
      if (response.data.success) {
        const dogsData = (response.data.dogs || []).map((dog: any) => {
          let kastriertValue = null;
          const rawValue = dog.kastriert;
          
          if (rawValue === true || rawValue === 1 || rawValue === 'Ja' || String(rawValue).toLowerCase() === 'true') {
            kastriertValue = true;
          } else if (rawValue === false || rawValue === 0 || rawValue === 'Nein' || String(rawValue).toLowerCase() === 'false') {
            kastriertValue = false;
          }
          
          // Check vaccination expiry
          const isVaccinationExpired = checkVaccinationExpiry(dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut);
          
          // Map the fields correctly based on what the API returns
          return {
            ...dog,
            // Ensure we have the correct ID field
            hunde_id: dog.hunde_id || dog.id,
            id: dog.id || dog.hunde_id,
            // Map name fields
            hundename: dog.hundename || dog.rufname,
            rufname: dog.rufname || dog.hundename,
            // Map date fields  
            geburtsdatum: dog.geburtsdatum || dog.geburtstag,
            geburtstag: dog.geburtstag || dog.geburtsdatum,
            // Image fields
            foto_path: dog.foto_path || dog.image_path,
            image_path: dog.image_path || dog.foto_path,
            kastriert: kastriertValue,
            imageTimestamp: dog.imageTimestamp || dog.image_path_updated || Date.now(),
            image_path_updated: dog.image_path_updated || dog.imageTimestamp || Date.now(),
            isVaccinationExpired
          };
        });
        
        setDogs(dogsData);
        
        // Check document status for each dog
        const statusChecks = dogsData.map(async (dog: any) => {
          const docStatus = await checkDogDocuments(dog.id || dog.hunde_id);
          return {
            dogId: dog.id || dog.hunde_id,
            missingDocuments: docStatus.missingDocuments,
            isVaccinationExpired: dog.isVaccinationExpired
          };
        });
        
        const allStatuses = await Promise.all(statusChecks);
        const statusMap = allStatuses.reduce((acc, status) => {
          acc[status.dogId] = {
            missingDocuments: status.missingDocuments,
            isVaccinationExpired: status.isVaccinationExpired
          };
          return acc;
        }, {} as {[key: string]: { missingDocuments: any[], isVaccinationExpired: boolean }});
        
        setDogDocumentStatus(statusMap);
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Error loading dogs:', error);
        showNotification('Fehler beim Laden der Hunde', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [checkDogDocuments, checkVaccinationExpiry]);

  useEffect(() => {
    loadDogs();
  }, [loadDogs]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setConfirmModal({
      visible: true,
      type: type,
      title: type === 'success' ? 'Erfolgreich' : 'Fehler',
      message: message,
      variant: 'single',
      onConfirm: () => {},
    });
  };

  const handleImageUpload = async (dogId: string, file: File) => {
    setUploadingImage(dogId);

    // Find the current dog data
    const currentDog = dogs.find(d => d.hunde_id === dogId);
    if (!currentDog) {
      showNotification('Hund nicht gefunden', 'error');
      setUploadingImage(null);
      return;
    }

    const formData = new FormData();

    // Add all current dog data to preserve it
    formData.append('rufname', currentDog.rufname || currentDog.hundename || '');
    formData.append('vollstaendiger_name', currentDog.vollstaendiger_name || ''); // Add missing field
    formData.append('rasse', currentDog.rasse || '');
    formData.append('geburtstag', currentDog.geburtstag || currentDog.geburtsdatum || '');
    formData.append('chipnummer', currentDog.chipnummer || currentDog.chip_nummer || '');
    formData.append('tassonummer', currentDog.tassonummer || currentDog.pass_nr || '');
    formData.append('tollwut_impfung_ablauf', currentDog.tollwut_impfung_ablauf || currentDog.impfstatus_tollwut || currentDog.tollwut || '');
    formData.append('insurance_name', currentDog.insurance_name || currentDog.versicherung || '');

    // Handle gender conversion - ALWAYS append a value
    if (currentDog.geschlecht === 1 || currentDog.geschlecht === 'weiblich' || currentDog.geschlecht === 'Weiblich') {
      formData.append('geschlecht', '1');
    } else if (currentDog.geschlecht === 0 || currentDog.geschlecht === 'männlich' || currentDog.geschlecht === 'Männlich') {
      formData.append('geschlecht', '0');
    } else {
      formData.append('geschlecht', ''); // Send empty string if unknown
    }

    // Handle kastriert conversion - ALWAYS append a value
    if (currentDog.kastriert === true || currentDog.kastriert === 1 || currentDog.kastriert === 'ja' || currentDog.kastriert === 'Ja' || currentDog.kastriert === '1' || currentDog.kastriert === 'true') {
      formData.append('kastriert', '1');
    } else if (currentDog.kastriert === false || currentDog.kastriert === 0 || currentDog.kastriert === 'nein' || currentDog.kastriert === 'Nein' || currentDog.kastriert === '0' || currentDog.kastriert === 'false') {
      formData.append('kastriert', '0');
    } else {
      formData.append('kastriert', ''); // Send empty string if unknown
    }

    // Handle hundebox conversion - ALWAYS append a value
    if (currentDog.hundebox === 1 || currentDog.hundebox === true || currentDog.hundebox === 'ja' || currentDog.hundebox === 'Ja') {
      formData.append('hundebox', '1');
    } else if (currentDog.hundebox === 0 || currentDog.hundebox === false || currentDog.hundebox === 'nein' || currentDog.hundebox === 'Nein') {
      formData.append('hundebox', '0');
    } else {
      formData.append('hundebox', ''); // Send empty string if unknown
    }

    // Add the new image
    formData.append('image', file);

    // Debug logging
    console.log('Uploading image for dog:', dogId);
    console.log('FormData entries:');
    // Use forEach instead of for...of for compatibility
    formData.forEach((value, key) => {
      console.log(key + ':', value);
    });

    try {
      // IMPORTANT: Delete Content-Type to let browser set multipart/form-data with boundary
      const response = await apiClient.put(`/api/dogs/${dogId}`, formData, {
        headers: {
          'Content-Type': undefined as any  // This forces axios to remove the default JSON content-type
        }
      });

      console.log('Response from server:', response.data);

      if (response.data.success) {
        showNotification('Foto erfolgreich hochgeladen', 'success');
        loadDogs();
      } else {
        console.error('Upload failed:', response.data);
        showNotification(response.data.message || 'Fehler beim Hochladen des Fotos', 'error');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      console.error('Error details:', error.response?.data);
      showNotification(error.response?.data?.message || 'Fehler beim Hochladen des Fotos', 'error');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleDeleteDog = async (dogId: string, dogName: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Hund löschen?',
      message: `Möchten Sie ${dogName} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const response = await apiClient.delete(`/api/dogs/${dogId}`);
          if (response.data.success) {
            showNotification(`${dogName} wurde erfolgreich gelöscht`, 'success');
            loadDogs();
          }
        } catch (error) {
          console.error('Error deleting dog:', error);
          showNotification('Fehler beim Löschen des Hundes', 'error');
        }
      },
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Nicht angegeben';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const calculateAge = (birthDate: string | undefined) => {
    if (!birthDate) return 'Alter unbekannt';
    try {
      const years = differenceInYears(new Date(), new Date(birthDate));
      if (years === 0) return 'Unter 1 Jahr';
      return `${years} ${years === 1 ? 'Jahr' : 'Jahre'} alt`;
    } catch {
      return 'Alter unbekannt';
    }
  };

  const calculateProfileCompletion = (dog: Dog, dogStatus: any) => {
    // Required fields: Rufname, Geburtstag, Geschlecht, Tollwutimpfdatum, Hundebox, Kastriert, Versicherung, Pflichtdokumente
    const rufname = dog.rufname || dog.hundename;
    const geburtstag = dog.geburtstag || dog.geburtsdatum;
    const geschlecht = dog.geschlecht;
    const tollwut = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
    const hundebox = dog.hundebox;
    const kastriert = dog.kastriert !== null && dog.kastriert !== undefined && dog.kastriert !== '';
    const versicherung = dog.versicherung || dog.insurance_name;
    const pflichtdokumente = dogStatus.missingDocuments.length === 0;
    
    console.log('Profile completion check for dog:', dog.rufname || dog.hundename, {
      rufname: !!rufname,
      geburtstag: !!geburtstag,
      geschlecht: !!geschlecht,
      tollwut: !!tollwut,
      hundebox: !!hundebox,
      kastriert: kastriert,
      'dog.kastriert': dog.kastriert,
      versicherung: !!versicherung,
      pflichtdokumente: pflichtdokumente,
      'missing docs count': dogStatus.missingDocuments.length
    });
    
    const requiredFields = [rufname, geburtstag, geschlecht, tollwut, hundebox, kastriert, versicherung, pflichtdokumente];
    const completedFields = requiredFields.filter(field => Boolean(field));
    const percentage = Math.round((completedFields.length / requiredFields.length) * 100);
    
    console.log('Completion:', completedFields.length, '/', requiredFields.length, '=', percentage + '%');
    
    return percentage;
  };

  const DogCard: React.FC<{ dog: Dog }> = ({ dog }) => {
    const isExpanded = expandedDog === dog.hunde_id;
    const dogStatus = dogDocumentStatus[dog.id || dog.hunde_id] || { missingDocuments: [], isVaccinationExpired: false };
    const completionPercentage = calculateProfileCompletion(dog, dogStatus);
    const [imageError, setImageError] = useState(false);
    
    
    return (
      <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-[#1c2432] dark:hover:border-gray-600">
        <div className="flex flex-col lg:flex-row">
          {/* Image Section */}
          <div className="group relative lg:w-[36%]">
            <div className="relative h-60 sm:h-72 lg:h-full lg:min-h-[390px]">
              {(dog.foto_path || dog.image_path) && !imageError ? (
                <img
                  src={`${BASE_URL}/api/dogs/image-url/${dog.hunde_id || dog.id}?token=${localStorage.getItem('jwt_token')}&ts=${dog.image_path_updated || dog.imageTimestamp || Date.now()}`}
                  alt={dog.rufname || dog.hundename}
                  className="w-full h-full object-cover"
                  onError={() => {
                    // If image fails to load, show fallback icon
                    setImageError(true);
                  }}
                />
              ) : (
                <div className="w-full h-full bg-primary-600 flex items-center justify-center">
                  <DogIcon className="h-24 w-24 text-white/50" />
                </div>
              )}
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-black/20" />
              
              {/* Profile Completion Ring */}
              <div className="absolute left-4 top-4">
                <div className="relative h-14 w-14 rounded-full bg-black/25 backdrop-blur-sm">
                  <svg className="h-14 w-14 -rotate-90 transform" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="2"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={completionPercentage === 100 ? "#10b981" : "#f59e0b"}
                      strokeWidth="2"
                      strokeDasharray={`${completionPercentage}, 100`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{completionPercentage}%</span>
                  </div>
                </div>
              </div>
              
              {/* Upload Button */}
              <label className="absolute right-4 top-4 cursor-pointer rounded-xl bg-white/90 p-2.5 text-gray-700 shadow-sm transition hover:bg-white">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(dog.hunde_id, file);
                  }}
                />
                {uploadingImage === dog.hunde_id ? (
                  <div className="animate-spin h-4 w-4 border-2 border-gray-700 border-t-transparent rounded-full" />
                ) : (
                  <CameraIcon className="h-4 w-4" />
                )}
              </label>
              
              {/* Dog Info Overlay */}
              <div className="absolute inset-x-0 bottom-0 bg-black/55 p-5 text-white backdrop-blur-[2px]">
                <h2 className="mb-1 text-2xl font-bold drop-shadow-sm">
                  {dog.rufname || dog.hundename}
                </h2>
                <p className="mb-3 text-sm text-white/85">
                  {dog.rasse || 'Rasse unbekannt'} • {calculateAge(dog.geburtstag || dog.geburtsdatum)}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm ${
                    completionPercentage === 100
                      ? 'bg-success-500/90 text-white'
                      : 'bg-warning-500/90 text-white'
                  }`}>
                    {completionPercentage === 100 ? (
                      <><CheckCircleIcon className="h-3.5 w-3.5 mr-1.5" /> Vollständig</>
                    ) : (
                      <><ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1.5" /> Unvollständig</>
                    )}
                  </span>
                  {dog.valid_tollwut && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-info-500/90 text-white backdrop-blur-sm">
                      <SyringeIcon className="h-3.5 w-3.5 mr-1.5" /> Geimpft
                    </span>
                  )}
                  {dogStatus.missingDocuments.length > 0 && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-error-600/90 text-white backdrop-blur-sm animate-pulse">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1.5" /> Dokumente fehlen
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Content Section */}
          <div className="bg-white p-5 dark:bg-[#1c2432] sm:p-6 lg:w-[64%]">

            {/* Info Sections */}
            <div className="space-y-5">
              {/* Basisdaten */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/20 sm:p-5">
                <h3 className="mb-3 flex items-center justify-between border-b-2 border-gray-200 pb-2 text-lg font-semibold text-primary-600 dark:border-gray-700 dark:text-primary-400">
                  <span className="flex items-center">
                    <IdentificationIcon className="h-5 w-5 mr-2 opacity-80" />
                    Basisdaten
                  </span>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-x-2">
                    <button
                      onClick={() => setEditingDog(dog)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-info-100 text-info-700 transition hover:bg-info-200 dark:bg-info-800/60 dark:text-info-300 dark:hover:bg-info-700/60"
                      title="Bearbeiten"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteDog(dog.hunde_id, dog.hundename)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-error-100 text-error-700 transition hover:bg-error-200 dark:bg-error-800/60 dark:text-error-300 dark:hover:bg-error-700/60"
                      title="Löschen"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </h3>
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-base text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="name" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">
                        Rufname:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </AttributeLabel>
                      <span className="ml-1">{dog.rufname || dog.hundename || <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="breed" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">Rasse:</AttributeLabel>
                      <span className="ml-1">{dog.rasse || <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="birthday" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">
                        Geburtstag:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </AttributeLabel>
                      <span className="ml-1">{formatDate(dog.geburtstag || dog.geburtsdatum) || <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    {(dog.geschlecht === 1 || dog.geschlecht === 'weiblich' || dog.geschlecht === 'Weiblich') ? 
                      <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">♀</span> :
                      (dog.geschlecht === 0 || dog.geschlecht === 'männlich' || dog.geschlecht === 'Männlich') ?
                      <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">♂</span> :
                      <span className="text-gray-500 mt-0.5 flex-shrink-0">?</span>
                    }
                    <div className="min-w-0 flex-1">
                      <strong className="font-semibold text-gray-700 dark:text-gray-100">
                        Geschlecht:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </strong>
                      <span className="ml-1">
                        {(dog.geschlecht === 1 || dog.geschlecht === 'weiblich' || dog.geschlecht === 'Weiblich') ? 'Weiblich' :
                         (dog.geschlecht === 0 || dog.geschlecht === 'männlich' || dog.geschlecht === 'Männlich') ? 'Männlich' :
                         <span className="text-warning-600 dark:text-warning-400 italic">Nicht angegeben</span>}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="neutered" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">
                        Kastriert:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </AttributeLabel>
                      <span className="ml-1">
                        {(dog.kastriert === true || dog.kastriert === 1) ? 'Ja' :
                         (dog.kastriert === false || dog.kastriert === 0) ? 'Nein' :
                         <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <AttributeLabel iconKey="chipNumber" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">Chipnummer:</AttributeLabel>
                      <span className="ml-1 break-words">{dog.chip_nummer || dog.chipnummer || <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="tassoNumber" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">Tasso-Nr.:</AttributeLabel>
                      <span className="ml-1">{dog.pass_nr || dog.tassonummer || <span className="text-warning-600 dark:text-warning-400 italic">Fehlt</span>}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gesundheit & Pflege */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/20 sm:p-5">
                <h3 className="mb-3 flex items-center border-b-2 border-gray-200 pb-2 text-lg font-semibold text-primary-600 dark:border-gray-700 dark:text-primary-400">
                  <DogIcon className="h-5 w-5 mr-2 opacity-80" />
                  Gesundheit & Pflege
                </h3>
                <div className="grid grid-cols-1 gap-y-3 text-base text-gray-600 dark:text-gray-300">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="vaccination" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">
                        Tollwutimpfung:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </AttributeLabel>
                      {(dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut) ? (
                        <>
                          <span className="ml-1">{formatDate(dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut)}</span>
                          {dogStatus.isVaccinationExpired && (
                            <div className="mt-1 text-error-600 dark:text-error-400 text-xs font-medium bg-error-50 dark:bg-error-900/20 px-2 py-1 rounded inline-flex items-center gap-1">
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              <span>Impfung abgelaufen - Neue Impfung erforderlich!</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="ml-1 italic text-warning-600 dark:text-warning-400">Ausstehend</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <AttributeLabel iconKey="liability" tone="primary" textClassName="font-semibold text-gray-700 dark:text-gray-100">
                        Versicherung:
                        <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                      </AttributeLabel>
                      {dog.versicherung || dog.insurance_name ? (
                        <span className="ml-1">{dog.versicherung || dog.insurance_name}</span>
                      ) : (
                        <div className="mt-1">
                          <span className="text-error-600 dark:text-error-400 text-xs font-medium bg-error-50 dark:bg-error-900/20 px-2 py-1 rounded inline-flex items-center gap-1">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            <span>Haftpflichtversicherung fehlt - Für Training erforderlich!</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Extras & Notizen */}
              {((dog.hundebox !== undefined && dog.hundebox !== null) || dog.extras) && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/20 sm:p-5">
                  <h3 className="mb-3 flex items-center border-b-2 border-gray-200 pb-2 text-lg font-semibold text-primary-600 dark:border-gray-700 dark:text-primary-400">
                    <PuzzlePieceIcon className="h-5 w-5 mr-2 opacity-80" />
                    Extras & Notizen
                  </h3>
                  <div className="space-y-2 text-base text-gray-600 dark:text-gray-300">
                    {dog.hundebox !== undefined && dog.hundebox !== null && (
                      <div className="flex items-start gap-2">
                        <HomeModernIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                          dog.hundebox === 1 || String(dog.hundebox).toLowerCase() === 'ja' ? 'text-success-600 dark:text-success-400' : 
                          dog.hundebox === 0 || String(dog.hundebox).toLowerCase() === 'nein' ? 'text-error-600 dark:text-error-400' : 
                          'text-gray-500 dark:text-gray-400'
                        }`} />
                        <p>
                          <strong className="font-semibold text-gray-700 dark:text-gray-100">
                            Hundebox vorhanden:
                            <span className="inline-block w-1.5 h-1.5 bg-success-500 rounded-full ml-1" title="Pflichtfeld für Vollständigkeit"></span>
                          </strong>
                          <span className="ml-1">
                            {dog.hundebox === 1 || String(dog.hundebox).toLowerCase() === 'ja' ? 'Ja' : 
                             dog.hundebox === 0 || String(dog.hundebox).toLowerCase() === 'nein' ? 'Nein' : 
                             'Nicht angegeben'}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <section className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-800/40">
              <button
                onClick={() => setExpandedDog(isExpanded ? null : dog.hunde_id)}
                className="flex w-full items-center gap-3 p-3.5 text-left transition hover:bg-gray-100 dark:hover:bg-gray-700/60"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Dokumente ausblenden" : "Dokumente anzeigen"}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                  <FolderOpenIcon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-gray-900 dark:text-white">Dokumente</span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                    {dogStatus.missingDocuments.length === 0
                      ? 'Pflichtdokumente vollständig'
                      : `${dogStatus.missingDocuments.length} ${dogStatus.missingDocuments.length === 1 ? 'Pflichtdokument fehlt' : 'Pflichtdokumente fehlen'}`}
                  </span>
                </span>
                <span className={`hidden rounded-full px-2.5 py-1 text-[10px] font-bold sm:inline-flex ${dogStatus.missingDocuments.length === 0 ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300' : 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'}`}>
                  {dogStatus.missingDocuments.length === 0 ? 'Vollständig' : 'Prüfen'}
                </span>
                {isExpanded ? <ChevronUpIcon className="h-5 w-5 shrink-0 text-gray-400" /> : <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-400" />}
              </button>

              {isExpanded && (
                <div className="border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
                  <MyDogDocuments dogId={dog.id || dog.hunde_id} />
                </div>
              )}
            </section>

            {/* Problems Alert */}
            {(() => {
              // Calculate if there are any actual warnings to show
              const hasVaccinationExpiry = dogStatus.isVaccinationExpired;
              const hasMissingDocuments = dogStatus.missingDocuments.length > 0;
              const hasLegacyVaccinationIssue = !dog.tollwut_impfung_ablauf && !dog.impfstatus_tollwut && !dogStatus.missingDocuments.some((doc: any) => doc.description.toLowerCase().includes('tollwut'));
              const hasLegacyInsuranceIssue = !dog.versicherung && !dog.insurance_name && !dogStatus.missingDocuments.some((doc: any) => doc.description.toLowerCase().includes('haftpflicht'));
              
              const hasAnyWarnings = hasVaccinationExpiry || hasMissingDocuments || hasLegacyVaccinationIssue || hasLegacyInsuranceIssue;
              
              return hasAnyWarnings;
            })() && (
              <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-warning-600 dark:text-warning-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-warning-800 dark:text-warning-200 mb-1">
                      Wichtige Hinweise für die Trainingsteilnahme:
                    </p>
                    <ul className="text-sm text-warning-700 dark:text-warning-300 space-y-1">
                      {/* Check vaccination expiry */}
                      {dogStatus.isVaccinationExpired && (
                        <li>• Tollwutimpfung ist abgelaufen</li>
                      )}
                      {/* Check missing required documents */}
                      {dogStatus.missingDocuments.map((doc: any, index: number) => (
                        <li key={index}>• {doc.description}</li>
                      ))}
                      {/* Legacy checks for backwards compatibility */}
                      {(() => {
                        // Check Tollwut vaccination if not already covered by API
                        if (!dog.tollwut_impfung_ablauf && !dog.impfstatus_tollwut && !dogStatus.missingDocuments.some((doc: any) => doc.description.toLowerCase().includes('tollwut'))) {
                          return <li>• Tollwutimpfung ist nicht dokumentiert</li>;
                        }
                        return null;
                      })()}
                      {(() => {
                        // Check insurance if not already covered by API
                        if (!dog.versicherung && !dog.insurance_name && !dogStatus.missingDocuments.some((doc: any) => doc.description.toLowerCase().includes('haftpflicht'))) {
                          return <li>• Haftpflichtversicherung ist nicht dokumentiert</li>;
                        }
                        return null;
                      })()}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Hint for Documents */}
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 bg-primary-500 rounded-full blur-xl opacity-30 animate-pulse" />
          </div>
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-primary-200 dark:border-primary-800 rounded-full animate-spin">
                  <div className="absolute top-0 left-0 w-4 h-4 bg-primary-500 rounded-full" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <IdentificationIcon className="h-8 w-8 text-primary-500 dark:text-primary-400 animate-pulse" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lade deine treuen Begleiter...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Hundeprofile werden geladen</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
      <header className="app-page-header hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:block">
        <div className="flex items-center justify-between gap-3 p-5 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <DogIcon className="h-7 w-7 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-gray-900 dark:text-white md:text-[28px]">Meine Hunde</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Profile, Gesundheit und Dokumente zentral verwalten</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex h-11 flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800"
            title="Hund hinzufügen"
          >
            <PlusIcon className="h-5 w-5" /> Hund hinzufügen
          </button>
        </div>
      </header>

      <div className="sm:hidden">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-base font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800"
          title="Hund hinzufügen"
        >
          <PlusIcon className="h-5 w-5" /> Hund hinzufügen
        </button>
      </div>

      {/* Dogs List */}
      {dogs.length === 0 ? (
        <div className="text-center py-8">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-primary-500 rounded-full blur-2xl opacity-20" />
            <div className="relative bg-primary-100 dark:bg-primary-900/30 rounded-full w-32 h-32 mx-auto flex items-center justify-center">
              <IdentificationIcon className="h-16 w-16 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Noch keine Fellnasen registriert
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-lg mx-auto text-base leading-relaxed">
            Füge deinen ersten Hund hinzu, um alle Informationen und Dokumente zentral zu verwalten. 
            Für Trainingseinheiten sind ein Tollwutimpfnachweis und eine Haftpflichtversicherung erforderlich.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center mx-auto"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Ersten Hund hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {dogs.map((dog, index) => (
            <div 
              key={dog.hunde_id}
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <DogCard dog={dog} />
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <DogModal
        dog={editingDog}
        isOpen={showAddModal || !!editingDog}
        onClose={() => {
          setShowAddModal(false);
          setEditingDog(undefined);
        }}
        onSave={(message) => {
          loadDogs();
          if (message) showNotification(message, 'success');
        }}
      />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

    </div>
  );
};

export default MyDogs;
